/**
 * Admin Error Logs Page
 *
 * View, filter, and manage error logs in the admin dashboard.
 */

import React, { useState } from "react";
import {
  listErrorLogs,
  getErrorLog,
  markErrorResolved,
  getErrorDigest,
  formatErrorAsJSON,
  formatErrorAsMarkdown,
  type ErrorLogSummary,
  type ErrorDigestResponse,
} from "../../api/admin-errors.js";
import { Skeleton } from "../../components/skeleton.js";

interface ErrorLogsPageState {
  page: number;
  limit: number;
  severity?: string;
  errorCode?: string;
  websiteId?: string;
}

const severityStyle = (
  severity: string,
): React.CSSProperties => {
  switch (severity) {
    case "critical":
      return { background: "#ffebee", color: "#c62828" };
    case "error":
      return { background: "#fff3e0", color: "#e65100" };
    case "warning":
      return { background: "#fff8e1", color: "#f57f17" };
    case "info":
      return { background: "#e3f2fd", color: "#1565c0" };
    default:
      return { background: "#f5f5f5", color: "#616161" };
  }
};

const priorityStyle = (
  priority: string,
): React.CSSProperties => {
  switch (priority) {
    case "P0":
      return { background: "#ffebee", color: "#c62828" };
    case "P1":
      return { background: "#fff3e0", color: "#e65100" };
    default:
      return { background: "#e3f2fd", color: "#1565c0" };
  }
};

const badgeStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 500,
};

export function ErrorLogsPage(): React.ReactElement {
  const [state, setState] = useState<ErrorLogsPageState>({
    page: 1,
    limit: 50,
  });

  const [errors, setErrors] = React.useState<ErrorLogSummary[]>([]);
  const [total, setTotal] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedError, setSelectedError] =
    React.useState<ErrorLogSummary | null>(null);
  const [showDetail, setShowDetail] = React.useState(false);
  const [copied, setCopied] = React.useState<string>("");
  const [digest, setDigest] = React.useState<ErrorDigestResponse | null>(null);
  const [showDigest, setShowDigest] = React.useState(false);
  const [digestLoading, setDigestLoading] = React.useState(false);
  const [digestCopied, setDigestCopied] = React.useState(false);

  // Load errors
  React.useEffect(() => {
    async function load() {
      setIsLoading(true);
      try {
        const result = await listErrorLogs({
          page: state.page,
          limit: state.limit,
          severity: state.severity,
          error_code: state.errorCode,
          website_id: state.websiteId,
        });
        setErrors(result.items);
        setTotal(result.total);
      } catch (err) {
        console.error("Failed to load error logs:", err);
        setErrors([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    }

    void load();
  }, [state]);

  function handleFilterChange(field: string, value: string) {
    setState((prev) => ({
      ...prev,
      [field]: value || undefined,
      page: 1, // Reset to first page on filter
    }));
  }

  async function handleViewDetail(error: ErrorLogSummary) {
    try {
      const full = await getErrorLog(error.id);
      setSelectedError(full);
      setShowDetail(true);
    } catch (err) {
      console.error("Failed to load error detail:", err);
    }
  }

  async function handleMarkResolved() {
    if (!selectedError) return;

    try {
      await markErrorResolved(selectedError.id);
      setShowDetail(false);
      setSelectedError(null);
      // Reload list
      setState((prev) => ({ ...prev, page: 1 }));
    } catch (err) {
      console.error("Failed to mark error as resolved:", err);
    }
  }

  function handleCopyJSON() {
    if (!selectedError) return;
    const json = formatErrorAsJSON(selectedError);
    navigator.clipboard
      .writeText(json)
      .then(() => {
        setCopied("json");
        setTimeout(() => setCopied(""), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy to clipboard:", err);
      });
  }

  function handleCopyMarkdown() {
    if (!selectedError) return;
    const md = formatErrorAsMarkdown(selectedError);
    navigator.clipboard
      .writeText(md)
      .then(() => {
        setCopied("markdown");
        setTimeout(() => setCopied(""), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy to clipboard:", err);
      });
  }

  async function handleLoadDigest() {
    setDigestLoading(true);
    try {
      const result = await getErrorDigest(24);
      setDigest(result);
      setShowDigest(true);
    } catch (err) {
      console.error("Failed to load digest:", err);
    } finally {
      setDigestLoading(false);
    }
  }

  function handleCopyDigest() {
    if (!digest) return;
    navigator.clipboard
      .writeText(digest.markdown)
      .then(() => {
        setDigestCopied(true);
        setTimeout(() => setDigestCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy digest:", err);
      });
  }

  const pages = Math.ceil(total / state.limit);

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "13px",
  };

  return (
    <div className="product-page">
      <div className="d-flex items-center justify-between mb-4">
        <div>
          <h1 className="page-title mb-1">Error Logs</h1>
          <p className="text-muted text-sm">
            Monitor and diagnose errors from your application.
          </p>
        </div>
        <button
          onClick={handleLoadDigest}
          disabled={digestLoading}
          className="btn btn--primary"
        >
          {digestLoading ? "Loading..." : "Daily Digest"}
        </button>
      </div>

      {/* Filters */}
      <section className="product-section mb-4">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
          <div>
            <label className="filter-label">
              Severity
            </label>
            <select
              value={state.severity ?? ""}
              onChange={(e) => handleFilterChange("severity", e.target.value)}
              style={inputStyle}
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>

          <div>
            <label className="filter-label">
              Error Code
            </label>
            <input
              type="text"
              placeholder="Search..."
              value={state.errorCode ?? ""}
              onChange={(e) => handleFilterChange("errorCode", e.target.value)}
              style={inputStyle}
            />
          </div>

          <div>
            <label className="filter-label">
              Website
            </label>
            <input
              type="text"
              placeholder="Website ID..."
              value={state.websiteId ?? ""}
              onChange={(e) => handleFilterChange("websiteId", e.target.value)}
              style={inputStyle}
            />
          </div>

          <div>
            <label className="filter-label">
              Per Page
            </label>
            <select
              value={state.limit}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  limit: parseInt(e.target.value, 10),
                  page: 1,
                }))
              }
              style={inputStyle}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </section>

      {/* Error List */}
      {isLoading ? (
        <Skeleton lines={3} />
      ) : errors.length === 0 ? (
        <section className="product-section text-center" style={{ padding: "32px" }}>
          <p className="text-muted">No errors logged</p>
        </section>
      ) : (
        <section className="product-section" style={{ padding: 0, overflow: "hidden" }}>
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Severity</th>
                  <th>Code</th>
                  <th>Message</th>
                  <th>Count</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((error) => (
                  <tr
                    key={error.id}
                    onClick={() => handleViewDetail(error)}
                    className="cursor-pointer"
                  >
                    <td className="whitespace-nowrap">
                      {new Date(error.timestamp).toLocaleString()}
                    </td>
                    <td>
                      <span style={{ ...badgeStyle, ...severityStyle(error.severity) }}>
                        {error.severity}
                      </span>
                    </td>
                    <td style={{ fontFamily: "monospace", color: "#555" }}>
                      {error.error_code}
                    </td>
                    <td style={{ color: "#666", maxWidth: "400px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {error.message}
                    </td>
                    <td className="fw-500">
                      {error.occurrence_count}
                    </td>
                    <td>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetail(error);
                        }}
                        className="btn btn--secondary btn--sm"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex items-center justify-between" style={{ borderTop: "1px solid #eee", padding: "12px 16px" }}>
            <span className="text-muted text-sm">
              Page {state.page} of {pages} ({total} total)
            </span>
            <div className="d-flex gap-1">
              <button
                disabled={state.page === 1}
                onClick={() =>
                  setState((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                className="btn btn--secondary btn--sm"
              >
                Previous
              </button>
              <button
                disabled={state.page === pages}
                onClick={() =>
                  setState((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                className="btn btn--secondary btn--sm"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Detail Modal */}
      {showDetail && selectedError && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "16px" }}>
          <div style={{ background: "#fff", borderRadius: "8px", maxWidth: "640px", width: "100%", maxHeight: "100vh", overflowY: "auto" }}>
            <div style={{ position: "sticky", top: 0, background: "#fff", borderBottom: "1px solid #eee", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <h2 className="section-title" style={{ marginBottom: "4px" }}>Error Details</h2>
                <p className="text-muted text-sm">
                  {selectedError.error_code}
                </p>
              </div>
              <button
                onClick={() => setShowDetail(false)}
                className="btn btn--secondary btn--sm"
              >
                Close
              </button>
            </div>

            <div style={{ padding: "24px" }}>
              {/* Basic Info */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", marginBottom: "24px" }}>
                <div>
                  <label className="filter-label">
                    Occurred
                  </label>
                  <p style={{ marginTop: "4px", fontSize: "13px" }}>
                    {new Date(selectedError.timestamp).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="filter-label">
                    Severity
                  </label>
                  <p style={{ marginTop: "4px", fontSize: "13px" }}>
                    <span style={{ ...badgeStyle, ...severityStyle(selectedError.severity) }}>
                      {selectedError.severity}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="filter-label">
                    Occurrences
                  </label>
                  <p style={{ marginTop: "4px", fontSize: "13px" }}>
                    {selectedError.occurrence_count}
                  </p>
                </div>
                <div>
                  <label className="filter-label">
                    Status
                  </label>
                  <p style={{ marginTop: "4px", fontSize: "13px" }}>
                    {selectedError.resolved ? "Resolved" : "Open"}
                  </p>
                </div>
              </div>

              {/* Message */}
              <div style={{ marginBottom: "24px" }}>
                <label className="filter-label">
                  Message
                </label>
                <p style={{ fontSize: "13px", color: "#666", background: "#f9f9f9", padding: "12px", borderRadius: "4px" }}>
                  {selectedError.message}
                </p>
              </div>

              {/* Stack Trace */}
              {selectedError.stack_trace && (
                <div style={{ marginBottom: "24px" }}>
                  <label className="filter-label">
                    Stack Trace
                  </label>
                  <pre style={{ fontSize: "12px", background: "#1e1e1e", color: "#d4d4d4", padding: "12px", borderRadius: "4px", overflowX: "auto", maxHeight: "192px" }}>
                    {selectedError.stack_trace}
                  </pre>
                </div>
              )}

              {/* Context */}
              {selectedError.context && Object.keys(selectedError.context).length > 0 && (
                <div style={{ marginBottom: "24px" }}>
                  <label className="filter-label">
                    Context
                  </label>
                  <pre style={{ fontSize: "12px", background: "#f9f9f9", padding: "12px", borderRadius: "4px", overflowX: "auto", maxHeight: "192px", border: "1px solid #eee" }}>
                    {JSON.stringify(selectedError.context, null, 2)}
                  </pre>
                </div>
              )}

              {/* Warnings */}
              {selectedError.warnings.length > 0 && (
                <div className="alert alert--warning" style={{ marginBottom: "24px" }}>
                  <p style={{ fontSize: "13px", fontWeight: 500, marginBottom: "4px" }}>
                    Warnings
                  </p>
                  <ul style={{ fontSize: "12px", margin: 0, paddingLeft: "16px" }}>
                    {selectedError.warnings.map((w, i) => (
                      <li key={i} style={{ marginBottom: "4px" }}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="d-flex gap-1" style={{ paddingTop: "16px", borderTop: "1px solid #eee" }}>
                <button
                  onClick={handleCopyJSON}
                  className="btn btn--secondary btn--sm"
                  style={copied === "json" ? { background: "#e8f5e9", color: "#2e7d32" } : {}}
                >
                  {copied === "json" ? "Copied JSON" : "Copy JSON"}
                </button>
                <button
                  onClick={handleCopyMarkdown}
                  className="btn btn--secondary btn--sm"
                  style={copied === "markdown" ? { background: "#e8f5e9", color: "#2e7d32" } : {}}
                >
                  {copied === "markdown"
                    ? "Copied Markdown"
                    : "Copy Markdown"}
                </button>
                {!selectedError.resolved && (
                  <button
                    onClick={handleMarkResolved}
                    className="btn btn--primary btn--sm"
                    style={{ marginLeft: "auto" }}
                  >
                    Mark Resolved
                  </button>
                )}
                <button
                  onClick={() => setShowDetail(false)}
                  className="btn btn--secondary btn--sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Digest Modal */}
      {showDigest && digest && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: "16px" }}>
          <div style={{ background: "#fff", borderRadius: "8px", maxWidth: "768px", width: "100%", maxHeight: "100vh", overflowY: "auto" }}>
            <div style={{ position: "sticky", top: 0, background: "#fff", borderBottom: "1px solid #eee", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <h2 className="section-title" style={{ marginBottom: "4px" }}>Daily Error Digest</h2>
                <p className="text-muted text-sm">
                  {digest.total_groups} groups, {digest.total_occurrences} total
                  occurrences (last 24h)
                </p>
              </div>
              <button
                onClick={() => setShowDigest(false)}
                className="btn btn--secondary btn--sm"
              >
                Close
              </button>
            </div>

            <div style={{ padding: "24px" }}>
              {digest.groups.length === 0 ? (
                <p className="text-muted text-center" style={{ padding: "16px 0" }}>
                  No errors in the last 24 hours.
                </p>
              ) : (
                digest.groups.map((g, i) => {
                  const priority =
                    g.priority_score >= 50
                      ? "P0"
                      : g.priority_score >= 30
                        ? "P1"
                        : "P2";

                  return (
                    <div
                      key={g.fingerprint}
                      style={{ border: "1px solid #eee", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "8px" }}>
                        <div className="d-flex items-center gap-1">
                          <span style={{ fontSize: "13px", fontWeight: 700, color: "#999" }}>
                            #{i + 1}
                          </span>
                          <span style={{ ...badgeStyle, ...priorityStyle(priority) }}>
                            {priority}
                          </span>
                          <span style={{ ...badgeStyle, ...severityStyle(g.severity) }}>
                            {g.severity}
                          </span>
                          <code style={{ fontSize: "13px", fontFamily: "monospace" }}>
                            {g.error_code}
                          </code>
                        </div>
                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#555" }}>
                          {g.occurrence_count}x
                        </span>
                      </div>

                      <p style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>{g.message}</p>

                      <div className="d-flex flex-wrap text-xs" style={{ gap: "12px", color: "#999" }}>
                        {g.surface && <span>Surface: {g.surface}</span>}
                        {g.route && <span>Route: {g.route}</span>}
                        {g.is_blocking && (
                          <span className="text-danger fw-500">
                            Blocking
                          </span>
                        )}
                        {g.unique_org_count > 1 && (
                          <span>{g.unique_org_count} orgs affected</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}

              <div className="d-flex gap-1" style={{ paddingTop: "16px", borderTop: "1px solid #eee" }}>
                <button
                  onClick={handleCopyDigest}
                  className="btn btn--secondary btn--sm"
                  style={digestCopied ? { background: "#e8f5e9", color: "#2e7d32" } : {}}
                >
                  {digestCopied ? "Copied!" : "Copy Markdown"}
                </button>
                <button
                  onClick={() => setShowDigest(false)}
                  className="btn btn--secondary btn--sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ErrorLogsPage;
